import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { createBooking } from "../common/api";
import { useTour } from "../context/TourContext";

function formatKRW(n) {
  return `₩${Number(n).toLocaleString("ko-KR")}`;
}

export default function CheckoutModal({
  open,
  onClose,
  tourId,
  title,
  date,
  persons,
  perPerson,
  total,
  optionValue,
}) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addParticipant } = useTour();

  const [email, setEmail] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (user?.email) setEmail(user.email);
  }, [user]);

  useEffect(() => {
    if (open) {
      setAgreed(false);
      setError(null);
    }
  }, [open]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  async function handleConfirm() {
    setError(null);
    if (!email) return setError("Please enter your email");
    if (!agreed) return setError("Please agree to the terms and privacy policy");

    setLoading(true);
    try {
      await createBooking({ tour_id: tourId, date, persons, option_value: optionValue, total });
      addParticipant(tourId, persons);
      onClose();
      navigate("/orders");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[3000] flex items-center justify-center px-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />

      <div className="relative w-full max-w-[440px] bg-white rounded-[24px] shadow-2xl overflow-hidden animate-[slideUp_0.25s_ease]">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
          <h2 className="text-[17px] font-bold text-gray-900">Payment Summary</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors text-gray-400"
          >
            ✕
          </button>
        </div>

        <div className="px-6 py-5 flex flex-col gap-5">

          {/* Booking summary */}
          <div className="bg-gray-50 rounded-[16px] p-4 flex flex-col gap-2.5">
            <p className="text-[15px] font-bold text-gray-900 m-0">{title}</p>
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              <Info icon="📅" text={date} />
              <Info icon="👤" text={`${persons} person(s)`} />
            </div>
            <div className="border-t border-gray-200 pt-2.5 flex justify-between items-center">
              <span className="text-[12px] text-gray-400">{formatKRW(perPerson)} × {persons} person(s)</span>
              <span className="text-[18px] font-bold text-gray-900">{formatKRW(total)}</span>
            </div>
          </div>

          {/* Email */}
          <div className="flex flex-col gap-1.5">
            <label className="text-[13px] font-medium text-gray-600">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="example@email.com"
              readOnly={!!user?.email}
              className={[
                "px-4 py-2.5 rounded-[10px] border text-[14px] text-gray-800 outline-none w-full transition-all",
                user?.email
                  ? "bg-gray-50 border-gray-200 cursor-default text-gray-500"
                  : "bg-white border-gray-200 focus:border-[#E8876A] focus:ring-2 focus:ring-[#E8876A]/20",
              ].join(" ")}
            />
          </div>

          {/* Terms agreement */}
          <label className="flex items-start gap-3 cursor-pointer">
            <div
              onClick={() => setAgreed((v) => !v)}
              className={[
                "mt-0.5 w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 transition-colors",
                agreed ? "bg-[#E8876A] border-[#E8876A]" : "border-gray-300 bg-white",
              ].join(" ")}
            >
              {agreed && (
                <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                  <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </div>
            <span className="text-[13px] text-gray-600 leading-relaxed">
              I agree to the{" "}
              <span
                className="text-[#E8876A] underline cursor-pointer"
                onClick={(e) => { e.stopPropagation(); window.open("/terms", "_blank"); }}
              >
                Terms of Service
              </span>
              {" "}and{" "}
              <span
                className="text-[#E8876A] underline cursor-pointer"
                onClick={(e) => { e.stopPropagation(); window.open("/privacy", "_blank"); }}
              >
                Privacy Policy
              </span>
            </span>
          </label>

          {error && <p className="text-[13px] text-red-400 m-0">{error}</p>}

          {/* Buttons */}
          <div className="flex gap-2 pt-1">
            <button
              onClick={onClose}
              className="flex-1 py-3 rounded-[12px] border border-gray-200 bg-white text-[14px] font-medium text-gray-600 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={loading}
              className="flex-[2] py-3 rounded-[12px] bg-[#E8876A] text-white text-[14px] font-semibold hover:bg-[#D96F52] transition-colors disabled:opacity-50 border-0"
            >
              {loading ? "Processing..." : `Pay ${formatKRW(total)}`}
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}

function Info({ icon, text }) {
  return (
    <span className="flex items-center gap-1 text-[13px] text-gray-500">
      {icon} {text}
    </span>
  );
}